/** @jsxRuntime classic */
/** @jsx jsx */
import { css, Global, jsx } from "@emotion/react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { Button } from "components/atoms/button/Button";
import { FullScreenDialog } from "components/common/Modal";
import { QrcodePrint } from "components/qrcode/QrcodePrint";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import { dbFirestore } from "firestore";
import { useI18n } from "hooks/i18n/i18n";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getSlashSeparatedDateTimeStringFromFbTimestamp } from "utility";
import {
  decodeTicketQRCodeValue,
  getVenuePayPrice,
  SHIPPING_PLACE_CODE,
} from "utility/ticket";
import { useQuery } from "utility/useQuery";
import * as z from "zod/mini";

import { QrcodeInfo } from "../../components/qrcode/QrcodeInfo";
import { loadingActions } from "../../modules/loading";
import { modalActions } from "../../modules/modal";
import {
  updateFirestoreDoc,
  fetchFirestoreDocument,
  fetchFirestoreCollectionMap,
} from "../../utility/firebase";
import { saveFailedUpdate } from "./failedUpdatesStorage";
import { getEnabledModes } from "./qrcodeReaderEnabledModes";
import { getReaderId } from "./qrcodeReaderId";
import { useIssuedTicketMap } from "./useIssuedTickets";
import { useSelectedRedeemTicketIdsAndEventIds } from "./useSelectedRedeemTicketIds";
import {
  playSuccessSound,
  useUnlockAudioOnUserGesture,
} from "./useSuccessSound";
import { vibrateError } from "./vibration";

import type { Store } from "../../store";
import type {
  ActiveTransaction,
  Event,
  IssuedTicket,
  ProductData,
} from "@spwn/types/firebase/firestore";
import type { IDetectedBarcode } from "@yudiel/react-qr-scanner";
import type { ComponentProps } from "react";

const isOfflineError = (error: unknown): boolean =>
  error instanceof Error &&
  "code" in error &&
  (error as { code: unknown }).code === "unavailable";

const successModalDisplayDurationMs = 1000;

const QrcodeReader: React.FC = () => {
  const { t } = useI18n();
  class DecordingTypeError extends TypeError {}
  const dispatch = useDispatch();
  useUnlockAudioOnUserGesture();
  const [uid, setLocalUid] = useState<string | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      setLocalUid(user?.uid ?? null);
      setIsAuthReady(true);
    });
    return unsubscribe;
  }, []);
  const query = useQuery();
  const queryEventId = query.get("eventId");
  const queryTicketId = query.get("ticketId");
  const {
    data: selectedRedeemTicketIdsAndEventIds,
    error: redeemTicketIdsError,
  } = useSelectedRedeemTicketIdsAndEventIds();
  const selectedRedeemTicketIds = selectedRedeemTicketIdsAndEventIds?.ticketIds;
  const selectedRedeemEventIds = selectedRedeemTicketIdsAndEventIds?.eventIds;

  const isOpenError = useSelector((state: Store) => state.modal.isOpenError);

  const {
    issuedTicketMap,
    error: issuedTicketsError,
    hasPendingWrites,
  } = useIssuedTicketMap(selectedRedeemEventIds);

  const classesQrcodeStyle = qrcodeStyle();

  const [qrcodeRowData, setQrcodeRowData] = useState<string>(""); //base64の状態 qrcodeReaderのパッケージで必要
  const [qrcodeJson, setQrcodeJson] =
    useState<ReturnType<typeof makePathDecodedJSON>>(); //qrcodeRowDataをmapに直したもの
  const [eventStatus, setEventStatus] = useState<{
    eventName: string;
    eventDate: string;
  }>();
  const [purchasedGoodsStatus, setPurchasedGoodsStatus] =
    useState<ComponentProps<typeof QrcodeInfo>["purchasedGoodsStatus"]>();
  const [isReadError, setIsReadError] = useState<boolean>(false); //qrcodeが指定されたフォーマットに即しているか モギリの際に使用

  const [isFireStorePermissionError, setIsFireStorePermissionError] =
    useState<boolean>(false);
  const [isPrint, setIsPrint] = useState<boolean>(false);
  const [successDialog, setSuccessDialog] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  const [errorDialog, setErrorDialog] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  const isDialogOpen = successDialog.open || errorDialog.open;

  const showErrorDialog = useCallback((message: string) => {
    setErrorDialog({ open: true, message });
    vibrateError();
  }, []);

  const showSuccessDialog = useCallback((message: string) => {
    setSuccessDialog({ open: true, message });
    setTimeout(() => {
      setSuccessDialog({ open: false, message: "" });
    }, successModalDisplayDurationMs);
  }, []);

  useEffect(() => {
    if (isAuthReady && !uid) {
      dispatch(modalActions.toggleLoginMethod());
    }
  }, [isAuthReady, uid, dispatch]);

  useEffect(() => {
    if (redeemTicketIdsError && !isOpenError) {
      showErrorDialog("❌もぎりグループの取得に失敗しました");
    }
  }, [redeemTicketIdsError, isOpenError, showErrorDialog]);

  useEffect(() => {
    if (issuedTicketsError && !isOpenError) {
      showErrorDialog("❌チケット情報の同期に失敗しました");
    }
  }, [issuedTicketsError, isOpenError, showErrorDialog]);

  const handleError = (err: unknown) => {
    console.error(err);
  };

  /**
   * 同じQRコードを連続で読み取らないためのクールダウン期間。
   * この期間内に同じ値がスキャンされた場合は無視する。
   */
  const SCAN_COOL_DOWN_MS = 4000;
  const lastScanRef = useRef<{ data: string; at: number } | null>(null);

  const handleScan = (detectedCodes: IDetectedBarcode[]) => {
    if (isDialogOpen) return;
    const data = detectedCodes[0]?.rawValue;
    if (!data) return;
    const now = Date.now();
    const last = lastScanRef.current;
    if (last && last.data === data && now - last.at < SCAN_COOL_DOWN_MS) {
      return;
    }
    lastScanRef.current = { data, at: now };
    setQrcodeRowData(data);
  };

  useEffect(() => {
    if (qrcodeRowData !== "") {
      (async () => {
        setIsFireStorePermissionError(false);
        try {
          dispatch(
            loadingActions.toggleLoading({ msg: t("qrcode.loadingQrcode") })
          );
          const data = makePathDecodedJSON(qrcodeRowData);
          if (data) {
            setQrcodeJson(data);
            await fetchTicketData(data);
            if (
              data.ticketType === "isChecked" ||
              data.ticketType === "isRedeemed"
            ) {
              await checkTicket(data);
            }
          }
        } catch (e) {
          showErrorDialog(`❌${t("cart.msg.unexpectedError")}`);
          console.log(e);
        } finally {
          dispatch(loadingActions.toggleLoading({}));
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrcodeRowData]); // TODO

  const initialState = () => {
    setQrcodeRowData("");
    // @ts-expect-error TS2345
    setQrcodeJson(null);
    // @ts-expect-error TS2345
    setEventStatus(null);
    // @ts-expect-error TS2345
    setPurchasedGoodsStatus(null);
    setIsReadError(false);
  };

  const fetchTicketData = async (ticketData: {
    ticketPath: string;
    eventId: string;
    ticketType: string;
    authCode: string;
    orderId: string;
  }) => {
    if (ticketData.ticketType !== "isGoods") {
      return;
    }
    try {
      const eventTicketData = await fetchFirestoreDocument<IssuedTicket>(
        ticketData.ticketPath
      );
      if (eventTicketData) {
        fetchEventInfo(ticketData.eventId);
        if (ticketData.ticketType === "isGoods") {
          const activetranData =
            await fetchFirestoreDocument<ActiveTransaction>(
              `settlement/${eventTicketData.owner}/activeTransactions/${ticketData.orderId}`
            );
          const goodsCollectionMap =
            await fetchFirestoreCollectionMap<ProductData>(
              `/goods/${ticketData.eventId}/goods`
            );
          if (
            !activetranData.isPurchased ||
            activetranData.status !== "PAYSUCCESS"
          ) {
            showErrorDialog(`❌${t("qrcode.errorQrcode3")}`);
            return;
          }
          if (activetranData.isCancelled) {
            showErrorDialog(`❌${t("qrcode.errorQrcode4")}`);
            return;
          }
          if (goodsCollectionMap && activetranData) {
            const goodsList = Object.keys(
              // @ts-expect-error TS2532
              activetranData.eventProductCountMap[ticketData.eventId].goods
            ).map((gid) => {
              return {
                // @ts-expect-error TS2532
                name: goodsCollectionMap[gid].name,
                // @ts-expect-error TS2532
                subClassName: goodsCollectionMap[gid].subClassName,
                // @ts-expect-error TS18047
                count: activetranData.productCountMap[gid],
                // @ts-expect-error TS2345
                price_jpy: getVenuePayPrice(goodsCollectionMap[gid]),
                // @ts-expect-error TS2532
                priority: goodsCollectionMap[gid].priority,
                // @ts-expect-error TS2532
                place: goodsCollectionMap[gid].place,
              };
            });
            const sortedGoodsList = goodsList
              .filter((el) => el.place !== SHIPPING_PLACE_CODE)
              .sort((a, b) => a.priority - b.priority);
            // @ts-expect-error TS2345
            setPurchasedGoodsStatus(sortedGoodsList);
          }
        }
      }
    } catch (e) {
      setIsFireStorePermissionError(true);
      console.log(e);
    }
  };

  // @ts-expect-error TS7006
  const fetchEventInfo = async (eid) => {
    const eventDocument = await fetchFirestoreDocument<Event>(
      `/events/${eid}/`
    );
    if (eventDocument) {
      const eventDataList = {
        eventName: eventDocument.title,
        eventDate: getSlashSeparatedDateTimeStringFromFbTimestamp(
          eventDocument.datetime
        ),
      };
      setEventStatus(eventDataList);
    }
  };

  const makePathDecodedJSON = (value: string) => {
    setIsReadError(false);
    try {
      const decoded = decodeTicketQRCodeValue(value);
      if (!decoded) {
        setIsReadError(true);
        return;
      }
      const { ticketType, eventId, ticketNumber, authCode, orderId, ticketId } =
        decoded;
      if (
        ticketType === undefined ||
        eventId === undefined ||
        ticketNumber === undefined ||
        authCode === undefined
      ) {
        setIsReadError(true);
        return;
      }
      const ticketTypeResult = z
        .enum(["isChecked", "isRedeemed", "isGoods"])
        .safeParse(ticketType);
      if (!ticketTypeResult.success) {
        setIsReadError(true);
        return;
      }

      return {
        ticketPath: `/tickets/${eventId}/issuedTickets/${ticketNumber}`,
        eventId,
        ticketNumber,
        ticketType: ticketTypeResult.data,
        authCode,
        orderId,
        ticketId,
      };
    } catch (e) {
      const hasMessage = (e: unknown): e is { message: string } =>
        // @ts-expect-error TS18047
        typeof e === "object" && "message" in e;
      // @ts-expect-error TS2769
      throw new DecordingTypeError(hasMessage(e) && e.message);
    }
  };

  const checkTicket = async (
    qrcodeData: NonNullable<ReturnType<typeof makePathDecodedJSON>>
  ) => {
    try {
      if (qrcodeData.ticketType === "isGoods") {
        dispatch(
          loadingActions.toggleLoading({ msg: t("qrcode.loadingQrcode") })
        );
      }

      const ticketNumber = qrcodeData?.ticketNumber;

      if (!ticketNumber) {
        showErrorDialog("❌チケット番号が取得できませんでした");
        return;
      }
      let issuedTicket = issuedTicketMap[ticketNumber];

      // 存在しない場合には、Firestoreから直接データを取得して確認する（リアルタイム同期の遅延が発生した場合）
      if (issuedTicket === undefined) {
        const _issuedTicket = await dbFirestore
          .issuedTickets(qrcodeData.eventId)
          .doc(qrcodeData.ticketNumber)
          .get()
          .then((doc) => doc.data());

        if (_issuedTicket) {
          issuedTicket = _issuedTicket;
        }
      }

      if (!issuedTicket) {
        showErrorDialog("❌該当のチケットが存在しませんでした");
        return;
      }

      // check if doc exist / authCodeの認証
      if (
        qrcodeData.authCode &&
        qrcodeData.authCode !== issuedTicket.authCode
      ) {
        throw new DecordingTypeError("Authcode error");
      }

      if (qrcodeData && queryEventId && queryEventId !== qrcodeData.eventId) {
        showErrorDialog("❌対象外のイベントのQRコードです");
        return;
      }
      if (
        qrcodeData &&
        queryTicketId &&
        queryTicketId !== qrcodeData.ticketId
      ) {
        showErrorDialog("❌対象外のチケットのQRコードです");
        return;
      }

      if (
        qrcodeData.ticketType === "isChecked" ||
        qrcodeData.ticketType === "isRedeemed"
      ) {
        const enabledModes = getEnabledModes();
        if (qrcodeData.ticketType === "isChecked" && !enabledModes.isChecked) {
          showErrorDialog(
            "❌対象外のQRコードです（この端末では特典引換のみ可能です）"
          );
          return;
        }
        if (
          qrcodeData.ticketType === "isRedeemed" &&
          !enabledModes.isRedeemed
        ) {
          showErrorDialog(
            "❌対象外のQRコードです（この端末ではチェックインのみ可能です）"
          );
          return;
        }

        /**
         * とりあえずチケットもぎり, 特典引換だけで検証
         * TODO: グッズ引換のQRコードも同様に検証する必要がある
         */
        if (selectedRedeemTicketIds === undefined) {
          showErrorDialog(
            "❌もぎり対象のチケットIDの取得がまだ完了していません"
          );
          return;
        }

        if (
          selectedRedeemTicketIds.length > 0 &&
          qrcodeData?.ticketId &&
          !selectedRedeemTicketIds.includes(qrcodeData.ticketId)
        ) {
          showErrorDialog("❌対象外のチケットのQRコードです");
          return;
        }
      }

      if (qrcodeData.ticketType === "isChecked" && !issuedTicket.isChecked) {
        const settingTicketData = {
          isChecked: true,
          checkedDateTime: firebase.firestore.FieldValue.serverTimestamp(),
          whoChecked: uid,
          qrcodeReaderId: getReaderId() !== "" ? getReaderId() : null,
        };

        /**
         * awaitしないことでオフライン時にupdateFirestoreDocのPromiseの完了を待たずに処理を進める
         * オフラインからオンラインに復帰した際に、Firestoreが自動的にローカルの変更をサーバーに反映してくれる
         * 失敗時(権限エラー等)はunhandled rejectionを防ぎつつエラーダイアログで通知する
         */
        void updateFirestoreDoc(qrcodeData.ticketPath, settingTicketData).catch(
          (e) => {
            console.error(e);
            saveFailedUpdate({
              ticketPath: qrcodeData.ticketPath,
              ownerUserId: issuedTicket.owner,
              readerUserId: uid,
              error: e,
            });
            showErrorDialog(`❌${t("qrcode.errorUpdateFailed")}`);
          }
        );

        void playSuccessSound();
        showSuccessDialog(`✅${t("qrcode.loadQrcode")}`);
        return;
      } else if (
        qrcodeData.ticketType === "isRedeemed" &&
        !issuedTicket.isRedeemed
      ) {
        const settingTicketData = {
          isRedeemed: true,
          whoRedeemed: uid,
          redeemedDateTime: firebase.firestore.FieldValue.serverTimestamp(),
          qrcodeReaderIdForRedeem: getReaderId() !== "" ? getReaderId() : null,
        };

        /**
         * awaitしないことでオフライン時にupdateFirestoreDocのPromiseの完了を待たずに処理を進める
         * オフラインからオンラインに復帰した際に、Firestoreが自動的にローカルの変更をサーバーに反映してくれる
         * 失敗時(権限エラー等)はunhandled rejectionを防ぎつつエラーダイアログで通知する
         */
        void updateFirestoreDoc(qrcodeData.ticketPath, settingTicketData).catch(
          (e) => {
            console.error(e);
            saveFailedUpdate({
              ticketPath: qrcodeData.ticketPath,
              ownerUserId: issuedTicket.owner,
              readerUserId: uid,
              error: e,
            });
            showErrorDialog(`❌${t("qrcode.errorUpdateFailed")}`);
          }
        );

        void playSuccessSound();
        showSuccessDialog(`✅${t("qrcode.redeem")}${t("qrcode.checkQrcode")}`);

        return;
      } else if (
        qrcodeData.ticketType === "isGoods" &&
        !issuedTicket.isRedeemed
      ) {
        const settingTicketData = {
          isRedeemed: true,
          whoRedeemed: uid,
          redeemedDateTime: firebase.firestore.FieldValue.serverTimestamp(),
        };

        await updateFirestoreDoc(qrcodeData.ticketPath, settingTicketData);
        showSuccessDialog(
          `${
            qrcodeData.ticketType === "isGoods"
              ? t("qrcode.goods")
              : t("qrcode.redeem")
          }${t("qrcode.checkQrcode")}`
        );
        return;
      } else {
        showErrorDialog(`⚠️${t("qrcode.useTicket")}`);
        return;
      }
    } catch (e) {
      if (e instanceof DecordingTypeError) {
        showErrorDialog(`❌${t("qrcode.errorQrcode1")}`);
        console.log(e.message);
      } else if (isOfflineError(e)) {
        showErrorDialog(
          `❌同期されたデータには該当のチケットが存在しませんでした。また、オフラインのため、チケット情報を取得できませんでした。通信環境をご確認ください。`
        );
        console.log(e);
      } else {
        showErrorDialog(`❌${t("qrcode.errorQrcode2")}`);
        console.log(e);
      }
    } finally {
      //読み込んだ後に初期化
      setQrcodeRowData("");
      // @ts-expect-error TS2345
      setPurchasedGoodsStatus(null);
      // @ts-expect-error TS2345
      setQrcodeJson(null);
      // @ts-expect-error TS2345
      setEventStatus(null);
      setIsReadError(false);
      if (qrcodeData.ticketType !== "isChecked") {
        dispatch(loadingActions.toggleLoading({}));
      }
    }
  };

  if (isPrint) {
    return (
      <QrcodePrint
        // @ts-expect-error TS2322
        eventStatus={eventStatus}
        // @ts-expect-error TS2322
        purchasedGoodsStatus={purchasedGoodsStatus}
        closePrint={() => setIsPrint(false)}
      />
    );
  }

  if (!(uid && !isFireStorePermissionError)) {
    return (
      <div css={classesQrcodeStyle.root}>
        <Global styles={globalStyle} />
        <div css={classesQrcodeStyle.headline}>
          <h2>QRCODE READER</h2>
        </div>

        <p css={classesQrcodeStyle.cautionText}>{t("qrcode.noLogin")}</p>
        <div
          css={css`
            max-width: 500px;
            margin: 0 auto;
          `}
        >
          <Button
            text={t("qrcode.login")}
            onClick={() => {
              dispatch(modalActions.toggleLoginMethod());
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div css={classesQrcodeStyle.root}>
      <Global styles={globalStyle} />
      <FullScreenDialog open={successDialog.open}>
        <div css={[resultDialogStyle.container, resultDialogStyle.success]}>
          <p css={resultDialogStyle.message}>{successDialog.message}</p>
        </div>
      </FullScreenDialog>
      <FullScreenDialog open={errorDialog.open}>
        <div css={[resultDialogStyle.container, resultDialogStyle.error]}>
          <p css={resultDialogStyle.message}>{errorDialog.message}</p>
          <div css={resultDialogStyle.buttonWrapper}>
            <Button
              // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
              text="確認完了"
              onClick={() => setErrorDialog({ open: false, message: "" })}
            />
          </div>
        </div>
      </FullScreenDialog>
      <div css={classesQrcodeStyle.headline}>
        <h2>QRCODE READER</h2>
      </div>

      <div css={classesQrcodeStyle.qrcodeReader}>
        <Scanner
          onScan={handleScan}
          onError={handleError}
          scanDelay={300}
          formats={["qr_code"]}
          allowMultiple={true}
          sound={false}
        />
      </div>

      {isReadError && <p>{t("qrcode.errorQrcode1")}</p>}

      {qrcodeJson && qrcodeJson.ticketType === "isGoods" && (
        <React.Fragment>
          {purchasedGoodsStatus && (
            <QrcodeInfo
              // @ts-expect-error TS2322
              eventStatus={eventStatus}
              purchasedGoodsStatus={purchasedGoodsStatus}
            />
          )}

          <div
            css={css`
              max-width: 500px;
              margin: 0 auto 16px;
            `}
          >
            <Button
              text={
                !(qrcodeRowData && purchasedGoodsStatus)
                  ? t("qrcode.loading")
                  : t("qrcode.cutTicket")
              }
              disabled={!purchasedGoodsStatus}
              onClick={async () => checkTicket(qrcodeJson)}
            />
          </div>

          <div
            css={css`
              max-width: 500px;
              margin: 0 auto 16px;
            `}
          >
            <Button
              // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
              text={"チケットを印刷する"}
              onClick={() => setIsPrint(true)}
            />
          </div>

          <div
            css={css`
              max-width: 500px;
              margin: 0 auto;
            `}
          >
            <Button
              text={t("qrcode.reset")}
              color={"#E84B0F"}
              disabled={!(qrcodeRowData && qrcodeJson)}
              onClick={initialState}
            />
          </div>
        </React.Fragment>
      )}

      {hasPendingWrites && (
        // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
        <p css={classesQrcodeStyle.pendingWritesText}>
          書き込み中…
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <br />
          （長く表示される場合はオフラインかもしれません。Wi-Fiの設定を確認してください）
        </p>
      )}
    </div>
  );
};

const globalStyle = css`
  #container {
    background-color: #000;
  }
`;

const qrcodeStyle = () => {
  return {
    root: css`
      width: 90%;
      max-width: 980px;
      min-height: calc(100vh - 255px);
      padding: 32px 0 80px;
      margin: 0 auto;
    `,
    headline: css`
      margin-bottom: 40px;
      letter-spacing: 1px;
      @media screen and (min-width: 768px) {
        display: flex;
        align-items: center;
        margin-bottom: 60px;
      }
      h2 {
        font-family: "din-condensed", sans-serif;
        font-weight: 400;
        font-style: normal;
        padding: 5px 0 0 15px;
        margin-bottom: 5px;
        color: #fff;
        font-size: 35px;
        line-height: 1em;
        border-left: 5px solid #00c2ae;
        @media screen and (min-width: 768px) {
          margin-bottom: 0;
          letter-spacing: 3px;
        }
      }
    `,
    cautionText: css`
      color: #ffffff;
      font-size: 24px;
      text-align: center;
      padding: 240px 0;
    `,
    infoText: css`
      color: #ffffff;
    `,
    qrcodeReader: css`
      width: 100%;
      max-width: 480px;
      margin: 0 auto 40px;
    `,
    pendingWritesText: css`
      max-width: 480px;
      color: #ffffff;
      font-size: 14px;
      text-align: center;
      border-radius: 4px;
    `,
  };
};

const resultDialogStyle = {
  container: css({
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    minHeight: 300,
    gap: 32,
  }),
  success: css({
    backgroundColor: "#e6f9f1",
    color: "#0a7a4b",
  }),
  error: css({
    backgroundColor: "#fdecec",
    color: "#b00020",
  }),
  message: css({
    fontSize: 24,
    fontWeight: "bold",
    margin: 0,
    textAlign: "center" as const,
  }),
  buttonWrapper: css({
    width: "100%",
    maxWidth: 300,
  }),
};

export default QrcodeReader;
